import { IQueueExecutionRequest } from "@/shared/Executions/models/IQueueExecutionRequest";
import { Plot } from "../models/Plot";
import { PlotField } from "../models/PlotField";
import { PlotFieldType } from "../models/PlotFieldType";
import { PlotSchema } from "../models/PlotSchema";
import { PlottingTypeGuardService } from "./PlottingTypeGuardService";
import { QueuePlotsArgs } from "@/shared/Executions/models/QueuePlotsArgs";
import { QueueExecutionArgs } from "@/shared/Executions/models/QueueExecutionArgs";
import { QueueExecutionRequestFactory } from "@/shared/Executions/services/QueueExecutionRequestFactory";


export class PlottingService {

    public static readonly CUSTOM_PLOT_TYPE = "unknown";

    public static getFlattenedPlot(plot: Plot, includeType: string | null): Plot {
        if (plot == null) throw new Error("plot is required");

        const plotObject = plot.configuration ?? plot;
        const { _type, ...properties } = plotObject || {};
        const flattenedPlot = { ...properties } as Plot;
        // Only include type data if at the top-level or configuration is a a known rule type
        if (includeType) {
            flattenedPlot.type = includeType
        }
        return flattenedPlot;

    }

    public static createPlotFromPlotSchema(plotSchema: PlotSchema): Plot {

        const newPlot: Plot = {
            id: this.generatePlotId(),
            _type: plotSchema.type,
            display_name: plotSchema.display_name,
            description: 'New plot configuration',
            category: plotSchema.category,
            configuration: {},
            schema: plotSchema
        }

        return newPlot;

    }

    public static createPlotFromPlotField(plotField: PlotField): Plot {

        const newPlot: Plot = {
            id: this.generatePlotId(),
            _type: plotField.field_type,
            display_name: plotField.display_name,
            display_field: plotField.display_field,
            description: plotField.description,
            category: "Default Value",
            configuration: {},
            schema: plotField
        }

        return newPlot;

    }

    private static generatePlotId(): string {
        const id = `plot-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
        return id;
    }

    public static initializePlotConfiguration(plot: Plot, fields: Array<PlotField>): Record<string, any> {
        const config: Record<string, any> = {}
        config.description = `${plot.display_name ?? "Plot"} Description`
        fields.forEach((field: PlotField) => {
            this.initializePlotConfigurationForField(field, config);
        })
        config._type = plot._type
        return config;
    }

    public static initializePlotFieldConfiguration(fields: Array<PlotField>): Record<string, any> {
        const config: Record<string, any> = {}
        //config.description = `${rule.display_name ?? "Rule"} Description`
        fields.forEach((field: PlotField) => {
            this.initializePlotConfigurationForField(field, config);
        })
        return config;
    }

    public static initializePlotConfigurationForField(field: PlotField, configuration?: Record<string, any>): Record<string, any> {
        const config: Record<string, any> = configuration || {};
        if (field.default_value !== undefined) {
            config[field.name] = field.default_value
        } else if (field.field_type === PlotFieldType.Boolean) {
            config[field.name] = false
        } else if (
            field.field_type === PlotFieldType.MultiSelect ||
            field.field_type === PlotFieldType.ColumnMultiSelect ||
            field.field_type === PlotFieldType.Array ||
            field.field_type === PlotFieldType.ArrayValues ||
            field.field_type === PlotFieldType.ColumnMultiSelectOrCreate
        ) {
            config[field.name] = []
        } else if (
            field.field_type === PlotFieldType.Object ||
            field.field_type === PlotFieldType.StringTemplate
        ) {
            config[field.name] = {}
        } else {
            config[field.name] = ''
        }
        return config;
    }

    public static createQueuePlotsArgs(
        parentExecutionId: string,
        flattenedPlots: Array<Plot>,
        name?: string): QueuePlotsArgs {

        const args = new QueuePlotsArgs(
            flattenedPlots,
            parentExecutionId,
            name
        )

        return args
    }

    public static getFormattedPlotsJson(parentExecutionId: string, executionName: string | undefined, plots: Array<Plot>): IQueueExecutionRequest {

        const flattenedRules = this.getFlattendPlots(plots);
        const args = PlottingService.createQueuePlotsArgs(parentExecutionId, flattenedRules, executionName);
        const queueExecutionArgs = new QueueExecutionArgs({ tenantId: '', userId: '', plotArgs: args });
        const payload = QueueExecutionRequestFactory.createQueueExecutionRequest(queueExecutionArgs);
        return payload;
    }

    public static getFlattendPlots(plots: Array<Plot>): Array<Plot> {

        const flattenedPlots = (plots || []).map((x) => PlottingService.getFlattenedPlot(x, x._type));
        this.recursivelyFlattenPlots(flattenedPlots);
        return flattenedPlots;

    }

    private static recursivelyFlattenPlots(plots: Array<Plot | Object>, visited = new Set<any>()) {
        for (const plot of plots) {
            if (visited.has(plot)) continue;
            visited.add(plot);

            for (const key of Object.keys(plot)) {
                const value = (plot as any)[key];

                if (Array.isArray(value)) {
                    const containsSubPlots =
                        value.length > 0 &&
                        value.every((item) => item && PlottingTypeGuardService.isPlot(item));

                    if (containsSubPlots) {
                        const flattenedSubPlots = value.map((subPlot: Plot) => {
                            const includeType = !this.isInternalSchemaType(subPlot._type) ? subPlot._type : null;
                            return this.getFlattenedPlot(subPlot, includeType);
                        });

                        (plot as any)[key] = flattenedSubPlots;
                        this.recursivelyFlattenPlots(flattenedSubPlots, visited);
                    }
                }
                else if (PlottingTypeGuardService.isNonEmptyPlotObject(value)) {

                    for (const nestedKey of Object.keys(value)) {
                        const nestedValue = value[nestedKey];

                        // Case 1: Direct plot object
                        if (PlottingTypeGuardService.isPlot(nestedValue)) {
                            const includeType = !this.isInternalSchemaType(nestedValue._type) ? nestedValue._type : null;
                            const flattened = this.getFlattenedPlot(nestedValue, includeType);
                            value[nestedKey] = flattened;

                            this.recursivelyFlattenPlots([flattened], visited);
                        }

                        // Case 2: Another nested non-empty plot-like object
                        else if (PlottingTypeGuardService.isNonEmptyPlotObject(nestedValue)) {
                            const coerced = nestedValue as Object;
                            this.recursivelyFlattenPlots([coerced], visited);
                        }

                        else if (Array.isArray(nestedValue)) {
                            const plotsArr = this.extractPlotsFromArray(nestedValue);
                            if (plotsArr.length > 0) {
                                const flattened = plotsArr.map(subPlot => {
                                    const includeType = !this.isInternalSchemaType(subPlot._type) ? subPlot._type : null;
                                    return this.getFlattenedPlot(subPlot, includeType);
                                });
                                value[nestedKey] = flattened;
                                this.recursivelyFlattenPlots(flattened, visited);
                            }
                        }
                    }
                }

            }
        }
    }

    private static extractPlotsFromArray(arr: unknown[]): Plot[] {
        return arr.filter(PlottingTypeGuardService.isPlot);
    }

    public static getFormattedPlotsJsonString(parentExecutionId: string, executionName: string | undefined, plots: Array<Plot>, plotsOnly: boolean = false): string {
        const payload = PlottingService.getFormattedPlotsJson(parentExecutionId, executionName, plots);
        return JSON.stringify((plotsOnly ? payload.customPlots : payload), null, 2)
    }

    public static tryGetPlotSchemaByType(type: string, plotSchemas: Array<PlotSchema>): PlotSchema | undefined {

        if (type == null) throw new Error("type is required");
        if (plotSchemas == null) throw new Error("plotSchemas are required");

        return plotSchemas.find(x => x.type === type) || plotSchemas.find(x => x.type === PlottingService.CUSTOM_PLOT_TYPE);

    }

    // Certain rule item_scheam structures use types like 'object' or 'array' that we do not want
    // persisted in output JSON.
    public static isInternalSchemaType(type: string): boolean {

        const internalTypes = ['object', 'array'];
        return internalTypes.indexOf(type?.toLowerCase().trim()) !== -1;

    }


}